/**
 * W-STICKY-NAVIGATION
 * Handles sticky navigation with automatic active state detection
 */

class StickyNavigation {
  constructor(section) {
    this.section = section;
    this.links = this.section.querySelectorAll(".sticky-navigation__link");
    this.observedSections = new Map();
    this.currentActiveLink = null;
    this.headerHeight = 0;

    this.init();
  }

  init() {
    // Calculate header height
    this.calculateHeaderHeight();

    // Setup links and collect sections to observe
    this.setupLinks();

    // Initialize Intersection Observer
    this.setupObserver();

    // Set initial active state
    this.setInitialActive();

    // Recalculate header height on resize
    window.addEventListener('resize', () => this.calculateHeaderHeight());
  }

  calculateHeaderHeight() {
    const header = document.querySelector('.shopify-section-header-sticky');
    this.headerHeight = header && !header.classList.contains('shopify-section-header-hidden')
      ? header.offsetHeight
      : 0;
  }

  setupLinks() {
    this.links.forEach((link) => {
      const href = link.getAttribute("href");

      // Only process anchor links
      if (!href || !href.startsWith("#")) return;

      // Find the target section
      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        this.observedSections.set(targetElement, link);
      }

      // Handle click smooth scroll
      link.addEventListener("click", (e) => {
        e.preventDefault();

        if (href.startsWith("#")) {
          const target = document.getElementById(targetId);

          if (target) {
            // Recalculate header height in case it changed
            this.calculateHeaderHeight();

            // Calculate total offset (header + sticky nav)
            const stickyNavSection = this.section.closest('#shopify-section-' + this.section.dataset.sectionId);
            const stickyNavHeight = stickyNavSection ? stickyNavSection.offsetHeight : 0;
            const totalOffset = this.headerHeight + stickyNavHeight;

            // For absolute positioned anchors, get the parent section's position
            let scrollTarget = target;
            const computedStyle = window.getComputedStyle(target);

            if (computedStyle.position === 'absolute' || target.offsetHeight === 0) {
              // If anchor is absolute or has no height, use parent element
              scrollTarget = target.parentElement;
            }

            // Get target position relative to document
            const targetRect = scrollTarget.getBoundingClientRect();
            const targetPosition = window.pageYOffset + targetRect.top - totalOffset;

            // Smooth scroll to target
            window.scrollTo({
              top: targetPosition,
              behavior: "smooth",
            });

            // Immediately set active link for better UX
            this.setActiveLink(link);
          }
        }
      });
    });
  }

  setupObserver() {
    // Calculate total offset dynamically
    const stickyNavHeight = this.section.closest('#shopify-section-' + this.section.dataset.sectionId)?.offsetHeight || 0;
    const totalOffset = this.headerHeight + stickyNavHeight;

    const options = {
      root: null,
      rootMargin: `-${totalOffset + 1}px 0px -50% 0px`,
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const link = this.observedSections.get(entry.target);
          if (link) {
            this.setActiveLink(link);
          }
        }
      });
    }, options);

    // Observe all target sections
    this.observedSections.forEach((link, section) => {
      this.observer.observe(section);
    });
  }

  setActiveLink(link) {
    // Remove active from all links
    this.links.forEach((l) => l.classList.remove("active"));

    // Add active to the current link
    link.classList.add("active");
    this.currentActiveLink = link;
  }

  setInitialActive() {
    // Check URL hash
    const hash = window.location.hash;
    if (hash) {
      const targetLink = Array.from(this.links).find(
        (link) => link.getAttribute("href") === hash
      );
      if (targetLink) {
        this.setActiveLink(targetLink);
        return;
      }
    }

    // Otherwise activate first link
    if (this.links.length > 0) {
      this.setActiveLink(this.links[0]);
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initStickyNavigation);
} else {
  initStickyNavigation();
}

function initStickyNavigation() {
  const sections = document.querySelectorAll(
    '[data-section-type="w-sticky-navigation"]'
  );
  sections.forEach((section) => {
    new StickyNavigation(section);
  });
}

// Handle Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target.querySelector(
      '[data-section-type="w-sticky-navigation"]'
    );
    if (section) {
      new StickyNavigation(section);
    }
  });
}

// Expose class globally for dynamic initialization
window.StickyNavigation = StickyNavigation;
